import { Component, OnInit } from '@angular/core';
import { ChartType, ChartDataSets, ChartOptions } from 'chart.js';
import * as Chart from 'chart.js';
import { Color, Label } from 'ng2-charts';
import { EstadisticasSistemaService } from 'src/app/services/estadisticas/estadisticas-sistema.service';

const barraValores = {
  id: 'barraValoresCredenciales',
  afterDatasetsDraw(chart: any) {
    if (
      chart.config.type !== 'bar' ||
      !chart.config.options ||
      !chart.config.options.mostrarValores
    ) {
      return;
    }
    const ctx = chart.ctx;
    chart.data.datasets.forEach((dataset: any, di: number) => {
      const meta = chart.getDatasetMeta(di);
      if (!meta || !meta.data) {
        return;
      }
      meta.data.forEach((bar: any, i: number) => {
        const value = dataset.data[i];
        if (value === null || value === undefined || value === 0) {
          return;
        }
        const model = bar._model;
        if (!model) {
          return;
        }
        ctx.save();
        ctx.fillStyle = '#2c3e50';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(
          new Intl.NumberFormat('de-DE').format(value),
          model.x,
          model.y - 4,
        );
        ctx.restore();
      });
    });
  },
};

let dataLabelsRegistrado = false;

@Component({
  selector: 'app-estadisticas-credenciales',
  templateUrl: './estadisticas-credenciales.component.html',
  styleUrls: ['./estadisticas-credenciales.component.scss'],
})
export class EstadisticasCredencialesComponent implements OnInit {
  isLoading = true;
  errorMsg = '';
  data: any = null;

  filtros = { fechaDesde: '', fechaHasta: '' };
  granularidad = 'dia';
  presetActivo = '';

  lineLabels: Label[] = [];
  lineData: ChartDataSets[] = [];
  lineColors: Color[] = [];
  lineOptions: ChartOptions = {};
  lineType: ChartType = 'bar';
  lineLegend = false;

  horaLabels: Label[] = [];
  horaData: ChartDataSets[] = [];
  horaColors: Color[] = [];
  horaOptions: ChartOptions = {};
  horaType: ChartType = 'bar';

  constructor(private service: EstadisticasSistemaService) {}

  ngOnInit(): void {
    if (!dataLabelsRegistrado) {
      Chart.plugins.register(barraValores as any);
      dataLabelsRegistrado = true;
    }
    this.aplicarPreset('ultimoMes');
  }

  async cargar(): Promise<void> {
    this.isLoading = true;
    this.errorMsg = '';
    try {
      const q: any = { granularidad: this.granularidad };
      if (this.filtros.fechaDesde) {
        q.fechaDesde = this.inicioDeDia(this.filtros.fechaDesde).toISOString();
      }
      if (this.filtros.fechaHasta) {
        q.fechaHasta = this.finDeDia(this.filtros.fechaHasta).toISOString();
      }
      this.data = await this.service.credenciales(q);
      this.buildChart();
      this.buildHoraChart();
    } catch (e) {
      console.error('Error cargando estadísticas de credenciales:', e);
      this.errorMsg =
        'No se pudieron cargar las estadísticas. Intente nuevamente.';
    } finally {
      this.isLoading = false;
    }
  }

  private inicioDeDia(d: string): Date {
    const [y, m, dd] = d.split('-').map(Number);
    return new Date(y, m - 1, dd, 0, 0, 0, 0);
  }

  private finDeDia(d: string): Date {
    const [y, m, dd] = d.split('-').map(Number);
    return new Date(y, m - 1, dd, 23, 59, 59, 999);
  }

  private toDateInput(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  aplicarPreset(preset: string): void {
    this.presetActivo = preset;
    const hoy = new Date();
    let desde: Date;
    let hasta: Date;
    switch (preset) {
      case 'hoy':
        desde = new Date(hoy);
        hasta = new Date(hoy);
        break;
      case '7dias':
        desde = new Date(hoy);
        desde.setDate(desde.getDate() - 6);
        hasta = new Date(hoy);
        break;
      case '30dias':
        desde = new Date(hoy);
        desde.setDate(desde.getDate() - 29);
        hasta = new Date(hoy);
        break;
      case 'mes':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        hasta = new Date(hoy);
        break;
      case 'ultimoMes':
        desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
        hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
        break;
      case 'todos':
        desde =
          this.data && this.data.desdeDatos
            ? new Date(this.data.desdeDatos)
            : new Date(hoy);
        hasta = new Date(hoy);
        break;
      default:
        return;
    }
    this.filtros.fechaDesde = this.toDateInput(desde);
    this.filtros.fechaHasta = this.toDateInput(hasta);
    this.cargar();
  }

  aplicarManual(): void {
    this.presetActivo = '';
    this.cargar();
  }

  limpiar(): void {
    this.presetActivo = '';
    this.filtros = { fechaDesde: '', fechaHasta: '' };
    this.cargar();
  }

  onGranularidad(): void {
    this.cargar();
  }

  buildChart(): void {
    if (!this.data || !this.data.serie) {
      return;
    }
    const serie = this.data.serie;
    const max = serie.length
      ? Math.max(...serie.map((s: any) => s.cantidad))
      : 0;
    this.lineLabels = serie.map((s: any) => s.periodo);
    this.lineData = [{ data: serie.map((s: any) => s.cantidad), label: 'Vistas' }];
    this.lineColors = [
      {
        backgroundColor: serie.map((s: any) =>
          max > 0 && s.cantidad === max ? '#f59e0b' : '#0288d1',
        ),
        borderColor: serie.map((s: any) =>
          max > 0 && s.cantidad === max ? '#f59e0b' : '#0288d1',
        ),
      },
    ];
    this.lineOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
      },
    };
  }

  buildHoraChart(): void {
    const porHora = this.data && this.data.porHora ? this.data.porHora : [];
    const pico = this.data && this.data.horaPico ? this.data.horaPico.hora : -1;
    this.horaLabels = porHora.map((h: any) => `${h.hora}h`);
    this.horaData = [
      { data: porHora.map((h: any) => h.cantidad), label: 'Vistas' },
    ];
    this.horaColors = [
      {
        backgroundColor: porHora.map((h: any) =>
          h.hora === pico ? '#f59e0b' : '#14b8a6',
        ),
        borderColor: porHora.map((h: any) =>
          h.hora === pico ? '#f59e0b' : '#14b8a6',
        ),
      },
    ];
    this.horaOptions = {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: false },
      mostrarValores: true,
      layout: { padding: { top: 24 } },
      scales: {
        xAxes: [{ ticks: { autoSkip: false } }],
        yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
      },
    } as any;
  }

  horaPicoTexto(): string {
    const p = this.data && this.data.horaPico ? this.data.horaPico : null;
    if (!p) {
      return '';
    }
    return `${p.hora}h (${p.cantidad} consultas)`;
  }

  get desdeDatosTexto(): string {
    const d =
      this.data && this.data.desdeDatos ? new Date(this.data.desdeDatos) : null;
    if (!d || isNaN(d.getTime())) {
      return '';
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }
}
